/// <amd-module name="Build/Admin/Pool/Components/SettingsContainer" />
import * as React from "react";

import { Card } from "VSSUI/Card";
import { Checkbox } from "VSSUI/Checkbox";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

import { newGuid } from "VSS/Core/Util/String";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import {
    ElasticPool,
    ResourceLimit,
    TaskAgentPool,
    TaskAgentPoolMaintenanceDefinition,
    TaskAgentPoolMaintenanceScheduleDays,
    OperatingSystemType
} from "DistributedTask/Client/Contracts/TaskAgent";

import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { ConfirmButtonType } from "Build/Admin/Common/Actions/ConfirmationDialogActions";
import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { ConfirmationDialog } from "Build/Admin/Common/Components/ConfirmationDialog";
import { ElasticPoolSettings } from "Build/Admin/Common/Components/ElasticPoolSettings";
import { ServiceEndpointConstants, StoreStatus } from "Build/Admin/Common/Constants";
import { getAzurePortalVmssUrl } from "Build/Admin/Common/ElasticPoolUtils";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { AzureSubscriptionSettingsPanel } from "Build/Admin/Pool/Components/AzureSubscriptionSettingsPanel";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";

import * as Resources from "Build/Admin/Pool/Resources";

export interface ISettingsContainerProps {
    actionCreator: PoolActionCreator;
    storesHub: StoresHub;
}

export interface ISettingsContainerState {
    hasProjectData: boolean;
    agentPool: TaskAgentPool | undefined;
    elasticPool: ElasticPool | undefined;
    serviceEndpoint: ServiceEndpoint | undefined;
    virtualMachineScaleSet: VirtualMachineScaleSet | undefined;
    azureSubscriptionSettingsPanelIsOpen: boolean;
    maintenanceJobEnabled: boolean;
    jobTimeout: string;
    concurrencyPercentage: string;
    historyToKeep: string;
    directoryExpiration: string;
    hourToRun: number;
    minuteToRun: number;
    daysToRun: TaskAgentPoolMaintenanceScheduleDays;
    jobTimeoutError: string;
    concurrencyPercentageError: string;
    historyToKeepError: string;
    directoryExpirationError: string;
    timeZoneId: string;
    recycleAfterEachUse: boolean;
    maxSavedNodeCount: number;
    agentInteractiveUI: boolean;
    maxCapacity: number | undefined;
    desiredIdle: number | undefined;
    timeToLiveMinutes: number | undefined;
    operatingSystem: OperatingSystemType | undefined;
    resourceLimits: ResourceLimit[];
}

interface DayCheckboxItem {
    value: TaskAgentPoolMaintenanceScheduleDays;
    label: string;
}

const LargeSpinner = () => <Spinner className="pipelines-pool-settings-spinner scroll-hidden flex-grow flex-self-center" size={SpinnerSize.large} />;

export class SettingsContainer extends VssComponent<ISettingsContainerProps, ISettingsContainerState> {
    private readonly _hourOptions: IListBoxItem[];
    private readonly _minuteOptions: IListBoxItem[];
    private _timezoneOptions: IListBoxItem[];

    private readonly _dayCheckboxItems: DayCheckboxItem[] = [
        { value: TaskAgentPoolMaintenanceScheduleDays.Monday, label: Resources.DayAbbreviationMonday },
        { value: TaskAgentPoolMaintenanceScheduleDays.Tuesday, label: Resources.DayAbbreviationTuesday },
        { value: TaskAgentPoolMaintenanceScheduleDays.Wednesday, label: Resources.DayAbbreviationWednesday },
        { value: TaskAgentPoolMaintenanceScheduleDays.Thursday, label: Resources.DayAbbreviationThursday },
        { value: TaskAgentPoolMaintenanceScheduleDays.Friday, label: Resources.DayAbbreviationFriday },
        { value: TaskAgentPoolMaintenanceScheduleDays.Saturday, label: Resources.DayAbbreviationSaturday },
        { value: TaskAgentPoolMaintenanceScheduleDays.Sunday, label: Resources.DayAbbreviationSunday }
    ];

    private _hourSelection = new DropdownSelection();
    private _minuteSelection = new DropdownSelection();
    private _timeZoneSelection = new DropdownSelection();

    constructor(props: ISettingsContainerProps, context: IVssComponentContext) {
        super(props, context);

        this.state = this._getStateFromStores();
        this._hourOptions = this._getTimeOptions(24);
        this._minuteOptions = this._getTimeOptions(60);
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.maintenanceStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.resourceLimitStore.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.maintenanceStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.resourceLimitStore.removeChangedListener(this._onStoreChanged);
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    public render(): JSX.Element {
        if (!this.state.agentPool
            || this.props.storesHub.maintenanceStore.state.status === StoreStatus.Loading) {
            return <LargeSpinner />;
        }

        return (
            <div className="pipelines-pool-settings page-content page-content-top">
                <ConditionalChildren renderChildren={this.state.elasticPool !== undefined}>
                    {this._renderElasticPoolSettings()}
                    <div className="card-spacer flex-grow" />
                    {this._renderAzureSubscriptionSettings()}
                </ConditionalChildren>
                <ConditionalChildren renderChildren={this.state.elasticPool !== undefined && !this.state.hasProjectData}>
                    <div className="card-spacer flex-grow" />
                </ConditionalChildren>
                <ConditionalChildren renderChildren={!this.state.hasProjectData}>
                    {this._renderAutoProvisionSettings()}
                    <div className="card-spacer flex-grow" />
                    {this._renderAutoUpdateSettings()}
                    <div className="card-spacer flex-grow" />
                    {this._renderMaintenanceJobSettings()}
                </ConditionalChildren>
                <ConfirmationDialog confirmationDialogStore={this.props.storesHub.confirmationDialogStore} />
                <AzureSubscriptionSettingsPanel
                    actionCreator={this.props.actionCreator}
                    storesHub={this.props.storesHub}
                    isOpen={this.state.azureSubscriptionSettingsPanelIsOpen}
                    onDismiss={() => this.setState({ azureSubscriptionSettingsPanelIsOpen: false })}
                    recycleAfterEachUse={this.state.elasticPool?.recycleAfterEachUse} />
            </div>
        );
    }

    private _renderElasticPoolSettings(): JSX.Element | undefined {
        const elasticPool: ElasticPool | undefined = this.state.elasticPool;

        if (elasticPool !== undefined) {
            return (
                <Card
                    className="elastic-pool-setting-card flex-grow"
                    titleProps={{ text: Resources.ElasticPoolSettings, size: TitleSize.Medium }}
                    headerCommandBarItems={this._getElasticPoolCommandBarItems()}>
                    <ElasticPoolSettings
                        showSaveFailedAgentCheckbox={true}
                        recycleAfterEachUse={this.state.recycleAfterEachUse}
                        saveFailedAgents={this.state.maxSavedNodeCount > 0}
                        maxCapacity={this.state.maxCapacity}
                        desiredIdle={this.state.desiredIdle}
                        agentInteractiveUI={this.state.agentInteractiveUI}
                        timeToLiveMinutes={this.state.timeToLiveMinutes}
                        operatingSystem={this.state.operatingSystem}
                        resourceLimits={this.state.resourceLimits}
                        virtualMachineScaleSet={this.state.virtualMachineScaleSet}
                        onSettingsChanged={this._onElasticPoolSettingsChanged} />
                </Card>
            );
        }
        else {
            return undefined;
        }
    }

    private _getElasticPoolCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                id: "undo",
                text: Resources.Undo,
                disabled: !this._elasticPoolIsDirty(),
                onActivate: () => {
                    this.setState(this._getStateFromStores())
                }
            },
            {
                important: true,
                id: "save",
                text: Resources.Save,
                disabled: !this._elasticPoolIsDirty() || !this._elasticPoolIsValid(),
                onActivate: () => {
                    if (this.state.elasticPool &&
                        this._elasticPoolIsValid() &&
                        this.state.maxCapacity !== undefined &&
                        this.state.desiredIdle !== undefined &&
                        this.state.timeToLiveMinutes !== undefined) {

                        this.props.actionCreator.updateElasticPoolSettings(
                            this.state.recycleAfterEachUse,
                            this.state.maxSavedNodeCount,
                            this.state.maxCapacity,
                            this.state.desiredIdle,
                            this.state.timeToLiveMinutes,
                            this.state.agentInteractiveUI);
                    }
                },
                isPrimary: true
            }
        ];
    }

    private _elasticPoolIsDirty(): boolean {
        if (this.state.elasticPool) {
            return this.state.recycleAfterEachUse !== this.state.elasticPool.recycleAfterEachUse ||
                this.state.maxSavedNodeCount !== this.state.elasticPool.maxSavedNodeCount ||
                this.state.maxCapacity !== this.state.elasticPool.maxCapacity ||
                this.state.desiredIdle !== this.state.elasticPool.desiredIdle ||
                this.state.timeToLiveMinutes !== this.state.elasticPool.timeToLiveMinutes ||
                this.state.agentInteractiveUI !== this.state.elasticPool.agentInteractiveUI;
        }
        else {
            return false;
        }
    }

    private _elasticPoolIsValid(): boolean {
        if (this.state.elasticPool) {
            return this.state.maxCapacity !== undefined &&
                this.state.maxCapacity >= 0 &&
                this.state.maxCapacity <= 1000 &&
                this.state.desiredIdle !== undefined &&
                this.state.desiredIdle >= 0 &&
                this.state.desiredIdle <= this.state.maxCapacity &&
                this.state.timeToLiveMinutes !== undefined &&
                this.state.timeToLiveMinutes >= 0 &&
                this.state.timeToLiveMinutes <= 10000;
        }
        else {
            return false;
        }
    }

    private _renderAzureSubscriptionSettings(): JSX.Element | undefined {
        const elasticPool: ElasticPool | undefined = this.state.elasticPool;
        const serviceEndpoint: ServiceEndpoint | undefined = this.state.serviceEndpoint;
        const virtualMachineScaleSet: VirtualMachineScaleSet | undefined = this.state.virtualMachineScaleSet;

        if (elasticPool) {
            return (
                <Card
                    className="azure-subscription-setting-card flex-grow"
                    titleProps={{ text: Resources.AzureSubscriptionSettings, size: TitleSize.Medium }}
                    headerCommandBarItems={this._getAzureSubscriptionCommandBarItems()}>
                    <div className="flex-column">
                        <div>
                            {Resources.ServiceConnectionNameLabel}
                            {this._getServiceConnectionNameContent(elasticPool, serviceEndpoint)}
                        </div>
                        <div>
                            {Resources.VirtualMachineScaleSetNameLabel}
                            {this._getVirtualMachineScaleSetNameContent(serviceEndpoint, virtualMachineScaleSet)}
                        </div>
                    </div>
                </Card>
            );
        }
        else {
            return undefined;
        }
    }

    private _getAzureSubscriptionCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                important: true,
                id: "edit",
                text: Resources.Edit,
                onActivate: () => {
                    this.setState({ azureSubscriptionSettingsPanelIsOpen: true });
                },
                isPrimary: true
            }
        ];
    }

    private _getServiceConnectionNameContent(elasticPool: ElasticPool, serviceEndpoint: ServiceEndpoint | undefined): JSX.Element {
        if (serviceEndpoint?.name) {
            const linkingService: IPoolAdminLinkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
            return <Link className="margin-left-4" href={linkingService.getServiceConnectionUrl(elasticPool)}>{serviceEndpoint.name}</Link>;
        }
        else {
            return <span className="margin-left-4">{Resources.ResourceUnavailable}</span>;
        }
    }

    private _getVirtualMachineScaleSetNameContent(serviceEndpoint: ServiceEndpoint | undefined, virtualMachineScaleSet: VirtualMachineScaleSet | undefined): JSX.Element {
        if (serviceEndpoint?.authorization?.parameters &&
            serviceEndpoint?.authorization?.parameters[ServiceEndpointConstants.AzureTenantId] &&
            virtualMachineScaleSet?.id &&
            virtualMachineScaleSet?.name) {
            return (
                <Link className="margin-left-4"
                    href={getAzurePortalVmssUrl(serviceEndpoint.authorization.parameters[ServiceEndpointConstants.AzureTenantId], virtualMachineScaleSet.id)}
                    target="_blank">
                    {virtualMachineScaleSet.name}
                </Link>
            );
        }
        else if (virtualMachineScaleSet?.name) {
            return <span className="margin-left-4">{virtualMachineScaleSet.name}</span>;
        }
        else {
            return <span className="margin-left-4">{Resources.ResourceUnavailable}</span>;
        }
    }

    private _renderAutoProvisionSettings(): JSX.Element {
        const autoProvision: boolean = this.state.agentPool !== undefined && this.state.agentPool.autoProvision;
        return (
            <Card
                className="auto-provision-setting-card flex-grow"
                titleProps={{ text: Resources.AutoProvisioning, size: TitleSize.Medium }}>
                <Toggle
                    onChange={this._onAutoProvisionChanged}
                    checked={autoProvision}
                    text={Resources.AutoProvisionSettingSwitchLabel} />
            </Card>
        );
    }

    private _renderAutoUpdateSettings(): JSX.Element {
        const enabled = this.state.agentPool !== undefined ? this.state.agentPool.autoUpdate : true;
        return (
            <Card
                className="auto-provision-setting-card flex-grow"
                titleProps={{ text: Resources.UpdateSettings, size: TitleSize.Medium }}>
                <Toggle
                    onChange={this._onAutoUpdateChanged}
                    checked={enabled}
                    text={Resources.AutoUpdate} />
            </Card>
        );
    }

    private _renderMaintenanceJobSettings(): JSX.Element {
        const {
            maintenanceJobEnabled,
            jobTimeout,
            concurrencyPercentage,
            historyToKeep,
            directoryExpiration,
            daysToRun,
            jobTimeoutError,
            concurrencyPercentageError,
            historyToKeepError,
            directoryExpirationError
        } = this.state;

        return (
            <Card
                className="maintenance-job-settings-card flex-grow"
                titleProps={{ text: Resources.MaintenanceJob, size: TitleSize.Medium }}
                headerCommandBarItems={this._getMaintenanceCommandBarItems()}>
                <div className="flex-column">
                    <Toggle
                        onChange={(ev, maintenanceJobEnabled) => this.setState({ maintenanceJobEnabled })}
                        checked={this.state.maintenanceJobEnabled}
                        text={Resources.MaintenanceJobEnableLabel} />
                    {
                        maintenanceJobEnabled &&
                        <div>
                            <Header className="maintenance-job-settings-header" title={Resources.Settings} titleSize={TitleSize.Medium} />
                            <FormItem className="maintenance-job-settings-form-item" message={jobTimeoutError} error={Boolean(jobTimeoutError)}>
                                <TextField
                                    className="maintenance-job-settings-text-field"
                                    label={Resources.MaintenanceJobTimeoutLabel}
                                    value={jobTimeout}
                                    required={true}
                                    onChange={this._setJobTimeout} />
                            </FormItem>
                            <FormItem className="maintenance-job-settings-form-item" message={concurrencyPercentageError} error={Boolean(concurrencyPercentageError)}>
                                <TextField
                                    className="maintenance-job-settings-text-field"
                                    label={Resources.MaintenanceJobConcurrencyLabel}
                                    value={concurrencyPercentage}
                                    required={true}
                                    onChange={this._setConcurrencyPercentage} />
                            </FormItem>
                            <FormItem className="maintenance-job-settings-form-item" message={historyToKeepError} error={Boolean(historyToKeepError)}>
                                <TextField
                                    className="maintenance-job-settings-text-field"
                                    label={Resources.MaintenanceJobHistoryLabel}
                                    value={historyToKeep}
                                    required={true}
                                    onChange={this._setHistoryToKeep} />
                            </FormItem>
                            <FormItem className="maintenance-job-settings-form-item" message={directoryExpirationError} error={Boolean(directoryExpirationError)}>
                                <TextField
                                    className="maintenance-job-settings-text-field"
                                    label={Resources.MaintenanceJobDirectoryExpirationLabel}
                                    value={directoryExpiration}
                                    required={true}
                                    onChange={this._setDirectoryExpiration} />
                            </FormItem>
                            <Header className="maintenance-job-settings-header" title={Resources.Scheduling} titleSize={TitleSize.Medium} />
                            <div className="flex-row maintenance-job-scheduling-time-container">
                                <span className={"maintenance-job-scheduling-time-label"}>{Resources.MaintenanceJobTimeLabel}</span>

                                <Dropdown
                                    className={"maintenance-job-scheduling-time-combobox"}
                                    calloutContentClassName={"maintenance-job-scheduling-time-callout"}
                                    ariaLabel={Resources.MaintenanceJobHourAriaLabel}
                                    items={this._hourOptions}
                                    selection={this._hourSelection}
                                    onSelect={(ev, item) => this.setState({ hourToRun: Number(item.id) })}
                                    showFilterBox={false} />

                                <span className={"maintenance-job-scheduling-time-label"}>{":"}</span>

                                <Dropdown
                                    className={"maintenance-job-scheduling-time-combobox"}
                                    calloutContentClassName={"maintenance-job-scheduling-time-callout"}
                                    ariaLabel={Resources.MaintenanceJobMinuteAriaLabel}
                                    items={this._minuteOptions}
                                    selection={this._minuteSelection}
                                    onSelect={(ev, item) => this.setState({ minuteToRun: Number(item.id) })}
                                    showFilterBox={false} />

                                <Dropdown
                                    className={"maintenance-job-scheduling-timezone-combobox"}
                                    calloutContentClassName={"maintenance-job-scheduling-timezone-callout"}
                                    ariaLabel={Resources.MaintenanceJobTimeZoneAriaLabel}
                                    items={this._timezoneOptions}
                                    selection={this._timeZoneSelection}
                                    onSelect={(ev, item) => this.setState({ timeZoneId: item.id })}
                                    showFilterBox={false} />
                            </div>
                            <div className="flex-row maintenance-job-scheduling-days-label-container">
                                <span className={"maintenance-job-scheduling-days-label"}>{Resources.MaintenanceJobDaysLabel}</span>
                            </div>
                            <div className="flex-row maintenance-job-scheduling-days-container">
                                {
                                    this._dayCheckboxItems.map(item =>
                                        <Checkbox
                                            key={item.value}
                                            checked={Boolean(daysToRun & item.value)}
                                            onChange={() => this.setState({ daysToRun: (daysToRun ^ item.value) })}
                                            label={item.label}
                                        />
                                    )
                                }
                            </div>
                        </div>
                    }
                </div>
            </Card>
        );
    }

    private _setJobTimeout = (ev: React.ChangeEvent, value: string): void => {
        this._onNumericTextFieldChanged(
            value,
            (state, value) => {
                state.jobTimeout = value;
            },
            (state, error) => {
                state.jobTimeoutError = error;
            }
        );
    }

    private _setConcurrencyPercentage = (ev: React.ChangeEvent, value: string): void => {
        this._onNumericTextFieldChanged(
            value,
            (state, value) => {
                state.concurrencyPercentage = value;
            },
            (state, error) => {
                state.concurrencyPercentageError = error;
            }
        );
    }

    private _setHistoryToKeep = (ev: React.ChangeEvent, value: string): void => {
        this._onNumericTextFieldChanged(
            value,
            (state, value) => {
                state.historyToKeep = value;
            },
            (state, error) => {
                state.historyToKeepError = error;
            }
        );
    }

    private _setDirectoryExpiration = (ev: React.ChangeEvent, value: string): void => {
        this._onNumericTextFieldChanged(
            value,
            (state, value) => {
                state.directoryExpiration = value;
            },
            (state, error) => {
                state.directoryExpirationError = error;
            }
        );
    }

    private _onNumericTextFieldChanged = (
        value: string,
        propertySetter: (state: ISettingsContainerState, val: string) => void,
        errorSetter: (state: ISettingsContainerState, val: string) => void
    ): void => {
        const newState = {} as ISettingsContainerState;
        propertySetter(newState, value);

        if (!value) {
            errorSetter(newState, Resources.MaintenanceJobRequiredError);
        }
        else if (!/^\d+$/.test(value)) {
            errorSetter(newState, Resources.MaintenanceJobMustBeIntegerError)
        }
        else {
            errorSetter(newState, "");
        }

        this.setState(newState);
    }

    private _onAutoProvisionChanged = (
        ev: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>,
        autoProvision: boolean
    ): void => {
        this.props.actionCreator.setPoolAutoProvisioning(autoProvision);
    }

    private _onAutoUpdateChanged = (
        ev: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>,
        enabled: boolean
    ): void => {
        if (!enabled) {
            this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
                this.state.agentPool as TaskAgentPool,
                Resources.DisableAgentAutoUpdate,
                Resources.DisableAgentAutoUpdateDetails,
                ConfirmButtonType.ConfirmDanger,
                this._confirmDisableAgentAutoUpdate,
                this._dismissConfirmationDialog);
        } else {
            this.props.actionCreator.setPoolAutoUpdate(enabled);
        }
    }

    private _confirmDisableAgentAutoUpdate = (): void => {
        this.props.actionCreator.setPoolAutoUpdate(false);
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }
    private _dismissConfirmationDialog = (): void => {
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _onElasticPoolSettingsChanged = (recycleAfterEachUse: boolean, saveFailedAgents: boolean, maxCapacity: number | undefined, desiredIdle: number | undefined, timeToLiveMinutes: number | undefined, agentInteractiveUI: boolean): void => {
        // Show a confirmation dialog when toggling recycle after each use
        if (this.state.elasticPool && (this.state.elasticPool.recycleAfterEachUse !== recycleAfterEachUse)) {
            this.setState({
                maxSavedNodeCount: saveFailedAgents ? 1 : 0,
                maxCapacity,
                desiredIdle,
                timeToLiveMinutes,
                agentInteractiveUI
            });
            this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
                this.state.agentPool as TaskAgentPool,
                Resources.RecycleAfterEachUseDialogTitle,
                Resources.RecycleAfterEachUseDialogBody,
                ConfirmButtonType.ConfirmDanger,
                this._confirmToggleRecycleAfterEachUse,
                this._dismissConfirmationDialog);
        }
        // Show a confirmation dialog when toggling agent interactive UI
        else if (this.state.elasticPool && (this.state.elasticPool.agentInteractiveUI !== agentInteractiveUI)) {
            this.setState({
                recycleAfterEachUse,
                maxSavedNodeCount: saveFailedAgents ? 1 : 0,
                maxCapacity,
                desiredIdle,
                timeToLiveMinutes
            });
            this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
                this.state.agentPool as TaskAgentPool,
                Resources.AgentInteractiveUIDialogTitle,
                Resources.AgentInteractiveUIDialogBody,
                ConfirmButtonType.ConfirmDanger,
                this._confirmToggleAgentInteractiveUI,
                this._dismissConfirmationDialog);
        }
        else {
            this.setState({
                recycleAfterEachUse,
                maxSavedNodeCount: saveFailedAgents ? 1 : 0,
                maxCapacity,
                desiredIdle,
                timeToLiveMinutes,
                agentInteractiveUI
            });
        }
    }

    private _confirmToggleAgentInteractiveUI = (): void => {
        this.setState({ agentInteractiveUI: this.state.elasticPool ? !this.state.elasticPool.agentInteractiveUI : false });
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _confirmToggleRecycleAfterEachUse = (): void => {
        this.setState({ recycleAfterEachUse: this.state.elasticPool ? !this.state.elasticPool.recycleAfterEachUse : false });
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _getStateFromStores(): ISettingsContainerState {
        let newState: ISettingsContainerState = {} as ISettingsContainerState;
        const existingDefinition = this.props.storesHub.maintenanceStore.state.maintenanceDefinitions[0];
        const agentPool = this.props.storesHub.selectionStore.state.selectedAgentPool;
        const elasticPool = this.props.storesHub.selectionStore.state.selectedElasticPool;
        const serviceEndpoint = this.props.storesHub.selectionStore.state.selectedServiceEndpoint;
        const virtualMachineScaleSet = this.props.storesHub.selectionStore.state.selectedVirtualMachineScaleSet;
        const resourceLimits = this.props.storesHub.resourceLimitStore.state.resourceLimits;
        const projectDataService = this.context.pageContext.getService<IProjectDataService>("IProjectDataService");

        if (!existingDefinition) {
            newState = {
                hasProjectData: projectDataService.hasProjectData(),
                agentPool,
                elasticPool,
                serviceEndpoint,
                virtualMachineScaleSet,
                azureSubscriptionSettingsPanelIsOpen: false,
                maintenanceJobEnabled: false,
                jobTimeout: "60",
                concurrencyPercentage: "25",
                historyToKeep: "10",
                directoryExpiration: "30",
                hourToRun: 1,
                minuteToRun: 0,
                daysToRun: TaskAgentPoolMaintenanceScheduleDays.None,
                jobTimeoutError: "",
                concurrencyPercentageError: "",
                historyToKeepError: "",
                directoryExpirationError: "",
                timeZoneId: "UTC",
                recycleAfterEachUse: elasticPool ? elasticPool.recycleAfterEachUse : false,
                maxSavedNodeCount: elasticPool ? elasticPool.maxSavedNodeCount : 0,
                maxCapacity: elasticPool ? elasticPool.maxCapacity : undefined,
                desiredIdle: elasticPool ? elasticPool.desiredIdle : undefined,
                timeToLiveMinutes: elasticPool ? elasticPool.timeToLiveMinutes : undefined,
                agentInteractiveUI: elasticPool ? elasticPool.agentInteractiveUI : false,
                operatingSystem: elasticPool ? elasticPool.osType : undefined,
                resourceLimits
            };
        } else {
            newState = {
                hasProjectData: projectDataService.hasProjectData(),
                agentPool,
                elasticPool,
                serviceEndpoint,
                virtualMachineScaleSet,
                azureSubscriptionSettingsPanelIsOpen: false,
                maintenanceJobEnabled: existingDefinition.enabled,
                jobTimeout: existingDefinition.jobTimeoutInMinutes.toString(),
                concurrencyPercentage: existingDefinition.maxConcurrentAgentsPercentage.toString(),
                historyToKeep: existingDefinition.retentionPolicy.numberOfHistoryRecordsToKeep.toString(),
                directoryExpiration: existingDefinition.options.workingDirectoryExpirationInDays.toString(),
                hourToRun: existingDefinition.scheduleSetting.startHours,
                minuteToRun: existingDefinition.scheduleSetting.startMinutes,
                daysToRun: existingDefinition.scheduleSetting.daysToBuild,
                jobTimeoutError: "",
                concurrencyPercentageError: "",
                historyToKeepError: "",
                directoryExpirationError: "",
                timeZoneId: existingDefinition.scheduleSetting.timeZoneId,
                recycleAfterEachUse: elasticPool ? elasticPool.recycleAfterEachUse : false,
                maxSavedNodeCount: elasticPool ? elasticPool.maxSavedNodeCount : 0,
                maxCapacity: elasticPool ? elasticPool.maxCapacity : undefined,
                desiredIdle: elasticPool ? elasticPool.desiredIdle : undefined,
                timeToLiveMinutes: elasticPool ? elasticPool.timeToLiveMinutes : undefined,
                agentInteractiveUI: elasticPool ? elasticPool.agentInteractiveUI : false,
                operatingSystem: elasticPool ? elasticPool.osType : undefined,
                resourceLimits
            };
        }

        this._hourSelection.select(newState.hourToRun);
        this._minuteSelection.select(newState.minuteToRun);

        if (!this._timezoneOptions && this.props.storesHub.maintenanceStore.state.validTimeZones) {
            this._timezoneOptions = this.props.storesHub.maintenanceStore.state.validTimeZones.map(tz => {
                return { id: tz.id, text: tz.name };
            });
        }

        const index = this._timezoneOptions.findIndex(tz => tz.id == newState.timeZoneId);
        this._timeZoneSelection.select(index);

        return newState;
    }

    private _createDefinitionFromSettings = (): TaskAgentPoolMaintenanceDefinition => {
        const currentDefinition: TaskAgentPoolMaintenanceDefinition | undefined = this.props.storesHub.maintenanceStore.state.maintenanceDefinitions[0];

        const definition = {
            id: currentDefinition ? currentDefinition.id : 0,
            enabled: this.state.maintenanceJobEnabled,
            jobTimeoutInMinutes: Number(this.state.jobTimeout),
            maxConcurrentAgentsPercentage: Number(this.state.concurrencyPercentage),
            retentionPolicy: {
                numberOfHistoryRecordsToKeep: Number(this.state.historyToKeep),
            },
            options: {
                workingDirectoryExpirationInDays: Number(this.state.directoryExpiration)
            },
            scheduleSetting: {
                scheduleJobId: currentDefinition && currentDefinition.scheduleSetting ? currentDefinition.scheduleSetting.scheduleJobId : newGuid(),
                startHours: this.state.hourToRun,
                startMinutes: this.state.minuteToRun,
                daysToBuild: this.state.daysToRun,
                timeZoneId: this.state.timeZoneId
            }
        } as TaskAgentPoolMaintenanceDefinition;

        return definition;
    }

    private _definitionIsDirty = (): boolean => {
        const currentDefinition = this.props.storesHub.maintenanceStore.state.maintenanceDefinitions[0];

        // If no current definition exists and we have flipped the switch to "enabled", consider the definition dirty.
        if (!currentDefinition) {
            return this.state.maintenanceJobEnabled;
        }

        return currentDefinition.enabled !== this.state.maintenanceJobEnabled
            || currentDefinition.jobTimeoutInMinutes.toString() !== this.state.jobTimeout
            || currentDefinition.maxConcurrentAgentsPercentage.toString() !== this.state.concurrencyPercentage
            || currentDefinition.retentionPolicy.numberOfHistoryRecordsToKeep.toString() !== this.state.historyToKeep
            || currentDefinition.options.workingDirectoryExpirationInDays.toString() !== this.state.directoryExpiration
            || currentDefinition.scheduleSetting.startHours !== this.state.hourToRun
            || currentDefinition.scheduleSetting.startMinutes !== this.state.minuteToRun
            || currentDefinition.scheduleSetting.daysToBuild !== this.state.daysToRun
            || currentDefinition.scheduleSetting.timeZoneId !== this.state.timeZoneId;
    }

    private _definitionIsValid = (): boolean => {
        return !this.state.jobTimeoutError
            && !this.state.concurrencyPercentageError
            && !this.state.directoryExpirationError
            && !this.state.historyToKeepError
            && this.state.daysToRun !== TaskAgentPoolMaintenanceScheduleDays.None;
    }

    private _getMaintenanceCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                id: "undo",
                text: Resources.Undo,
                disabled: !this._definitionIsDirty(),
                onActivate: () => {
                    this.setState(this._getStateFromStores());
                }
            },
            {
                important: true,
                id: "save",
                text: Resources.Save,
                disabled: !this._definitionIsDirty() || !this._definitionIsValid(),
                onActivate: () => {
                    if (this.state.agentPool) {
                        const definition = this._createDefinitionFromSettings();
                        this.props.actionCreator.createOrUpdateMaintenanceDefinition(definition, this.state.agentPool.id);
                    }
                },
                isPrimary: true,
                tooltipProps: { text: this.state.maintenanceJobEnabled && this.state.daysToRun === TaskAgentPoolMaintenanceScheduleDays.None ? Resources.NoDaySelected : undefined }
            }
        ];
    }

    private _getTimeOptions = (length: number): IListBoxItem[] => {
        const items: IListBoxItem[] = [];
        for (let i: number = 0; i < length; i++) {
            items.push({ id: i.toString(), text: i < 10 ? "0" + i.toString() : i.toString() });
        }
        return items;
    }
}
